import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { View } from 'react-native';
import MapControls from './MapControls';
import useStyles from '../../hooks/styles-hook';
import useCustomTheme from '../../hooks/theme-hook';
import pinImage from '../../../assets/pin_smallhit.png';
import pinReferral from '../../../assets/pin-referral.png';
import { referralPinWebFrame, referralPinWebImage } from './referralPinLayout';
import pinGreyImage from '../../../assets/pin_grey_smallhit.png';
import pinSelectedImage from '../../../assets/pin-selected-smallhit.png';
import pinBolt from '../../../assets/pin-bolt.png';
import pinBoltBw from '../../../assets/pin-bolt-bw.png';
import pinSmallBlue from '../../../assets/pin-small-blue.png';
import pinSmallOrange from '../../../assets/pin-small-orange.png';
import pinAgeRestricted from '../../../assets/pin-age-restricted-smallhit.png';
import pinAgeRestrictedBolt from '../../../assets/pin-age-restricted-bolt.png';
import { MapLocation, MapMarker, MapProps } from '../../models/MapModels';
import Constants from 'expo-constants';
import { MissionType } from '../../api/api.g';
import { Cluster, ClusterStats, MarkerClusterer, NoopAlgorithm, Renderer, SuperClusterViewportAlgorithm } from '@googlemaps/markerclusterer';
import { googleDarkMapStyle, googleLightMapStyle } from './mapThemeStyles';
import burgundyPinImage from '../../../assets/pin-burgundy-smallhit.png';
import { getMarkerRenderKey } from './markerRenderKey';
import { shouldRenderMarkerOutsideCluster } from './markerClustering';

const myGoogle = (typeof window !== 'undefined' ? (window as any).google : null) as any;

const parser = typeof document !== 'undefined' ? new DOMParser() : null;

const pinCurrentLocationImage = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512"><path d="M464 256A208 208 0 1 0 48 256a208 208 0 1 0 416 0zM0 256a256 256 0 1 1 512 0A256 256 0 1 1 0 256zm256-96a96 96 0 1 1 0 192 96 96 0 1 1 0-192z"/></svg>`;

function createSafeViewportAlgorithm(options: { radius?: number; maxZoom?: number }) {
  const viewport = new SuperClusterViewportAlgorithm({ radius: 250, maxZoom: 18, ...options } as any);
  const noop = new NoopAlgorithm({});
  return {
    calculate(input: { markers: unknown[]; map: { getZoom(): number | undefined; getBounds(): unknown }; mapCanvasProjection: unknown }) {
      const zoom = input.map.getZoom();
      const bounds = input.map.getBounds();
      if (zoom == null || bounds == null || typeof zoom !== 'number' || !Number.isFinite(zoom)) {
        return noop.calculate(input as Parameters<NoopAlgorithm['calculate']>[0]);
      }
      try {
        return viewport.calculate(input as Parameters<SuperClusterViewportAlgorithm['calculate']>[0]);
      } catch {
        return noop.calculate(input as Parameters<NoopAlgorithm['calculate']>[0]);
      }
    },
  };
}

class CustomRenderer implements Renderer {
  render(cluster: Cluster, _stats: ClusterStats, map: any) {
    if (!myGoogle) return new (class {})();
    const count = cluster.count;
    const clusterElement = document.createElement('div');
    clusterElement.style.position = 'relative';
    clusterElement.style.width = '60px';
    clusterElement.style.height = '60px';
    clusterElement.style.display = 'flex';
    clusterElement.style.alignItems = 'center';
    clusterElement.style.justifyContent = 'center';
    clusterElement.style.color = 'rgba(255,255,255,0.9)';
    clusterElement.style.fontSize = '16px';
    clusterElement.style.fontWeight = '600';

    clusterElement.innerHTML = `
      <svg fill="rgb(67, 164, 158)" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 240 240" style="position:absolute;inset:0;width:60px;height:60px;">
        <circle cx="120" cy="120" opacity=".6" r="90" />
        <circle cx="120" cy="120" opacity=".3" r="100" />
        <circle cx="120" cy="120" opacity=".2" r="110" />
        <circle cx="120" cy="120" opacity=".1" r="120" />
      </svg>
      <span style="position:relative;z-index:1;">${count}</span>
    `;

    return new myGoogle.maps.marker.AdvancedMarkerElement({
      map,
      position: cluster.position,
      content: clusterElement,
      title: `Cluster of ${count} markers`,
      zIndex: 1000 + count,
    });
  }
}

function getMarkerMissionType(marker: any): number | undefined {
  const raw = marker?.missionType ?? marker?.content?.dataset?.missionType;
  const n = Number(raw);
  return Number.isFinite(n) ? n : undefined;
}

function getDefaultPinImage(marker: any, isFlashMission?: boolean): string {
  if (marker?.isReferral || marker?.content?.dataset?.isReferral === 'true') return pinReferral;
  const defaultPin = marker?.defaultPin ?? marker?.content?.dataset?.defaultPin;
  const competitorCircuitRole = marker?.competitorCircuitRole ?? marker?.content?.dataset?.competitorCircuitRole;
  if (competitorCircuitRole === 'main') return burgundyPinImage;
  if (competitorCircuitRole === 'competitor') return pinImage;
  if (getMarkerMissionType(marker) === MissionType.Airport) return pinSmallBlue;
  if (defaultPin === 'ageRestricted') {
    return isFlashMission ? pinAgeRestrictedBolt : pinAgeRestricted;
  }
  if (isFlashMission) return defaultPin === 'grey' ? pinBoltBw : pinBolt;
  return defaultPin === 'grey' ? pinGreyImage : pinImage;
}

const SELECTED_GLOW = 'drop-shadow(0 0 12px rgba(245, 214, 74, 0.9))';
const AIRPORT_PIN_ICON_TOP = 'calc(50% - 13px)';
const AIRPORT_PIN_ICON_LEFT = 'calc(50% - 9px)';
const hasFiniteCoordinates = (location?: MapLocation) => Number.isFinite(location?.lat) && Number.isFinite(location?.lng);

function getPinElement(obj?: MapMarker): HTMLDivElement | null {
  if (typeof document === 'undefined' || !parser) return null;
  const pinElement = document.createElement('div');
  if (obj?.accessibilityLabel) {
    pinElement.setAttribute('aria-label', obj.accessibilityLabel);
  }
  pinElement.style.position = 'relative';
  pinElement.style.outline = 'none';
  pinElement.style.border = 'none';
  pinElement.style.background = 'transparent';
  pinElement.style.transform = 'scale(0.9)';
  pinElement.style.transformOrigin = 'center bottom';
  if (obj?.defaultPin) pinElement.dataset.defaultPin = obj.defaultPin;
  pinElement.dataset.isReferral = String(!!obj?.isReferral);
  if (obj?.missionType !== undefined) pinElement.dataset.missionType = String(obj.missionType);
  if (obj?.competitorCircuitRole) pinElement.dataset.competitorCircuitRole = obj.competitorCircuitRole;
  const isAirport = obj?.missionType === MissionType.Airport;
  const isCompetitorCircuitMarker = obj?.competitorCircuitRole !== undefined;
  const img = document.createElement('img');
  img.src = getDefaultPinImage(obj, obj?.isFlashMission);
  if (obj?.isReferral) {
    pinElement.style.width = `${referralPinWebFrame.width}px`;
    pinElement.style.height = `${referralPinWebFrame.height}px`;
    img.style.position = 'absolute';
    img.style.width = `${referralPinWebImage.width}px`;
    img.style.height = `${referralPinWebImage.height}px`;
    img.style.left = `${referralPinWebImage.left}px`;
    img.style.top = `${referralPinWebImage.top}px`;
    img.style.pointerEvents = 'none';
  }
  pinElement.appendChild(img);
  if (obj?.svgIcon && !obj.isReferral) {
    const svg = parser.parseFromString(obj.svgIcon, 'image/svg+xml').documentElement;
    svg.style.fill = '#000000';
    svg.style.position = 'absolute';
    svg.style.height = isAirport ? '20px' : isCompetitorCircuitMarker ? '22px' : '16px';
    svg.style.width = isAirport ? '20px' : isCompetitorCircuitMarker ? '22px' : '16px';
    if (isAirport) {
      svg.style.transform = 'rotate(-45deg)';
      svg.style.transformOrigin = 'center center';
    }
    if (obj.isFlashMission) {
      svg.style.top = 'calc(50% - 36px)';
      svg.style.left = 'calc(50% - 8px)';
    } else if (isAirport) {
      svg.style.top = AIRPORT_PIN_ICON_TOP;
      svg.style.left = AIRPORT_PIN_ICON_LEFT;
    } else if (isCompetitorCircuitMarker) {
      svg.style.top = 'calc(50% - 17px)';
      svg.style.left = 'calc(50% - 11px)';
    } else {
      svg.style.top = 'calc(50% - 14px)';
      svg.style.left = 'calc(50% - 8px)';
    }
    pinElement.appendChild(svg);
  }
  return pinElement;
}

function setMarkerVisualDefault(marker: any, isFlash?: boolean) {
  const content = marker?.content as HTMLElement | undefined;
  if (content) {
    content.style.filter = '';
    if (marker.isReferral || content.dataset?.isReferral === 'true') {
      content.style.transform = 'scale(0.9)';
      marker.zIndex = undefined;
    }
  }
  if (marker?.content?.children?.[0]) (marker.content.children[0] as HTMLImageElement).src = getDefaultPinImage(marker, isFlash);
  const svg = content?.querySelector?.('svg') as SVGElement | null;
  if (svg && getMarkerMissionType(marker) === MissionType.Airport) {
    svg.style.top = AIRPORT_PIN_ICON_TOP;
    svg.style.left = AIRPORT_PIN_ICON_LEFT;
  }
}

function setMarkerVisualSelected(marker: any, isFlash?: boolean) {
  const content = marker.content as HTMLElement;
  if (marker.isReferral || content?.dataset?.isReferral === 'true') {
    (content.children[0] as HTMLImageElement).src = pinReferral;
    content.style.filter = '';
    content.style.transformOrigin = '50% 100%';
    // The ordinary marker canvas already uses 0.9; enlarge it by 8%.
    content.style.transform = 'scale(0.972)';
    marker.zIndex = 100_000;
    return;
  }
  const isAirport = getMarkerMissionType(marker) === MissionType.Airport;
  const circuitRole = marker.competitorCircuitRole ?? content?.dataset?.competitorCircuitRole;
  (marker.content.children[0] as HTMLImageElement).src = circuitRole === 'competitor'
    ? getDefaultPinImage(marker, isFlash)
    : circuitRole === 'main' ? pinSelectedImage : isAirport ? pinSmallOrange : isFlash ? pinBolt : pinSelectedImage;
  if (content) content.style.filter = circuitRole === 'competitor' ? '' : SELECTED_GLOW;
  const svg = content?.querySelector?.('svg') as SVGElement | null;
  if (svg && isAirport) {
    svg.style.top = AIRPORT_PIN_ICON_TOP;
    svg.style.left = AIRPORT_PIN_ICON_LEFT;
  }
}

const WebMap = (props: MapProps) => {
  const theme = useCustomTheme();
  const styles = useStyles((s) => s.customMap);
  const mapRef = useRef<any>(null);
  const mapViewRef = useRef<View>(null);
  const markerClustererRef = useRef<MarkerClusterer | null>(null);
  const markersArrayRef = useRef<any[]>([]);
  const userLocationMarkerRef = useRef<any>(null);
  const selectedMarkerRef = useRef<any>(null);
  const selectedFlashRef = useRef(false);
  const initializedThemeIsDarkRef = useRef<boolean | undefined>(undefined);
  const initializedMapIdRef = useRef<string | undefined>(undefined);
  const airportActiveRef = useRef(false);
  const boundsTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastViewportCommandIdRef = useRef<number | undefined>(undefined);
  const ignoreBoundsChangeRef = useRef(false);
  const ignoreNextBoundsFromClusterRef = useRef(false);
  const viewportCommandTokenRef = useRef<number | undefined>(undefined);
  const viewportIgnoreFallbackTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const suppressBoundsCallbackRef = useRef(false);
  const removeMapGestureListenersRef = useRef<(() => void) | undefined>(undefined);
  const callbackRefs = useRef({
    markerClick: props.markerClick,
    onFilterMissionList: props.onFilterMissionList,
    onClusterClick: props.onClusterClick,
    onAirportBannerPress: props.onAirportBannerPress,
    onBoundsChange: props.onBoundsChange,
    onViewportChange: props.onViewportChange,
    onUserInteractionComplete: props.onUserInteractionComplete,
    onUserInteractionStart: props.onUserInteractionStart,
    onCleanSelection: props.onCleanSelection,
  });
  callbackRefs.current.markerClick = props.markerClick;
  callbackRefs.current.onFilterMissionList = props.onFilterMissionList;
  callbackRefs.current.onClusterClick = props.onClusterClick;
  callbackRefs.current.onAirportBannerPress = props.onAirportBannerPress;
  callbackRefs.current.onBoundsChange = props.onBoundsChange;
  callbackRefs.current.onViewportChange = props.onViewportChange;
  callbackRefs.current.onUserInteractionComplete = props.onUserInteractionComplete;
  callbackRefs.current.onUserInteractionStart = props.onUserInteractionStart;
  callbackRefs.current.onCleanSelection = props.onCleanSelection;

  const markerItems = props.markerItems;
  const [selectedId, setSelectedId] = useState<string | undefined>(props.selectedId);
  const selectedIdRef = useRef(selectedId);
  selectedIdRef.current = selectedId;
  const [mapReady, setMapReady] = useState(false);
  const [mapGeneration, setMapGeneration] = useState(0);

  const refPosition = props.referencePosition ?? Constants.expoConfig.extra.defautlLocation;
  const panToOriginPosition = props.panToOriginPosition ?? refPosition;
  const userLocationPosition = hasFiniteCoordinates(props.userLocationPosition) ? props.userLocationPosition : undefined;
  const initialZoom = props.zoom ?? 9;
  const mapThemeStyles = useMemo(() => (theme.isDark ? googleDarkMapStyle : googleLightMapStyle), [theme.isDark]);
  const mapColorScheme = useMemo(() => {
    if (!myGoogle?.maps?.ColorScheme) {
      return theme.isDark ? 'DARK' : 'LIGHT';
    }

    return theme.isDark ? myGoogle.maps.ColorScheme.DARK : myGoogle.maps.ColorScheme.LIGHT;
  }, [theme.isDark]);

  useEffect(() => {
    setSelectedId(props.selectedId);
  }, [props.selectedId]);

  const handleUserInteractionStart = useCallback(() => {
    // A real gesture takes ownership immediately, even during an automatic move.
    ignoreBoundsChangeRef.current = false;
    suppressBoundsCallbackRef.current = false;
    ignoreNextBoundsFromClusterRef.current = false;
    viewportCommandTokenRef.current = undefined;
    if (viewportIgnoreFallbackTimerRef.current) {
      clearTimeout(viewportIgnoreFallbackTimerRef.current);
      viewportIgnoreFallbackTimerRef.current = null;
    }
    callbackRefs.current.onUserInteractionStart?.();
  }, []);

  // Initialize / refresh map when parent asks for it.
  useEffect(() => {
    if (!myGoogle) return;

    const shouldReinitialize =
      !mapReady || props.refreshMap || initializedThemeIsDarkRef.current !== theme.isDark || initializedMapIdRef.current !== props.mapId;

    if (!shouldReinitialize) return;

    const t = setTimeout(() => {
      const div = mapViewRef.current as any;
      if (!div) return;
      if (mapRef.current) {
        myGoogle.maps.event.clearInstanceListeners(mapRef.current);
      }
      removeMapGestureListenersRef.current?.();
      if (markerClustererRef.current) {
        markerClustererRef.current.clearMarkers(true);
        (markerClustererRef.current as MarkerClusterer & { setMap(map: null): void }).setMap(null);
        myGoogle.maps.event.clearInstanceListeners(markerClustererRef.current);
        markerClustererRef.current = null;
      }
      markersArrayRef.current.forEach((marker) => {
        myGoogle.maps.event.clearInstanceListeners(marker);
        marker.map = null;
      });
      markersArrayRef.current = [];
      selectedMarkerRef.current = null;
      clearUserLocationMarker();
      mapRef.current = new myGoogle.maps.Map(div, {
        center: { lat: refPosition.lat, lng: refPosition.lng },
        zoom: initialZoom,
        disableDefaultUI: true,
        mapId: props.mapId,
        colorScheme: mapColorScheme,
        ...(props.mapId ? {} : { styles: mapThemeStyles }),
        clickableIcons: false,
        isFractionalZoomEnabled: false,
      });
      initializedThemeIsDarkRef.current = theme.isDark;
      initializedMapIdRef.current = props.mapId;
      lastViewportCommandIdRef.current = undefined;
      mapRef.current.addListener('dragstart', handleUserInteractionStart);
      const handleMapKey = (event: KeyboardEvent) => {
        if (['+', '=', '-', 'ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight', 'PageUp', 'PageDown', 'Home', 'End'].includes(event.key)) {
          handleUserInteractionStart();
        }
      };
      div.addEventListener('wheel', handleUserInteractionStart, { passive: true, capture: true });
      div.addEventListener('dblclick', handleUserInteractionStart, true);
      div.addEventListener('keydown', handleMapKey, true);
      removeMapGestureListenersRef.current = () => {
        div.removeEventListener('wheel', handleUserInteractionStart, true);
        div.removeEventListener('dblclick', handleUserInteractionStart, true);
        div.removeEventListener('keydown', handleMapKey, true);
      };
      mapRef.current.addListener('click', () => {
        if (selectedMarkerRef.current || airportActiveRef.current) {
          clearSelectedMarker();
          setAirportActive(false);
          setSelectedId(undefined);
          callbackRefs.current.onCleanSelection?.(true);
        }
      });
      mapRef.current.addListener('bounds_changed', () => {
        if (boundsTimerRef.current) clearTimeout(boundsTimerRef.current);
        if (!callbackRefs.current.onBoundsChange && !callbackRefs.current.onViewportChange) return;
        // Classify this change now, not after idle clears the programmatic flags.
        const shouldSuppressBoundsCallback = ignoreBoundsChangeRef.current && suppressBoundsCallbackRef.current;
        const shouldNotifyUserInteraction = !ignoreBoundsChangeRef.current && !ignoreNextBoundsFromClusterRef.current;
        boundsTimerRef.current = setTimeout(() => {
          try {
            const bounds = mapRef.current?.getBounds?.();
            if (bounds) {
              const ne = bounds.getNorthEast();
              const sw = bounds.getSouthWest();
              const center = mapRef.current?.getCenter?.();
              if (center) callbackRefs.current.onViewportChange?.(ne.lat(), sw.lat(), sw.lng(), ne.lng(), { lat: center.lat(), lng: center.lng() });
              if (ignoreNextBoundsFromClusterRef.current) {
                ignoreNextBoundsFromClusterRef.current = false;
              }
              if (shouldSuppressBoundsCallback) {
                return;
              }
              if (shouldNotifyUserInteraction) {
                callbackRefs.current.onUserInteractionComplete?.();
              }
              callbackRefs.current.onBoundsChange?.(ne.lat(), sw.lat(), sw.lng(), ne.lng());
            }
          } catch (e) {
            console.error('WebMap bounds', e);
          }
        }, 300);
      });
      setMapReady(true);
      setMapGeneration((value) => value + 1);
      props.onMapRefreshed?.();
    }, 100);
    return () => clearTimeout(t);
  }, [mapReady, props.refreshMap, refPosition.lat, refPosition.lng, initialZoom, props.onMapRefreshed, props.mapId, mapThemeStyles, mapColorScheme, theme.isDark]);

  // Programmatic viewport commands (fit bounds / center+zoom)
  useEffect(() => {
    const command = props.viewportCommand;
    if (!command || !mapReady || !mapRef.current || command.id === lastViewportCommandIdRef.current) {
      return;
    }

    lastViewportCommandIdRef.current = command.id;
    viewportCommandTokenRef.current = command.id;
    ignoreBoundsChangeRef.current = true;
    suppressBoundsCallbackRef.current = !!command.suppressBoundsCallback;

    if (viewportIgnoreFallbackTimerRef.current) {
      clearTimeout(viewportIgnoreFallbackTimerRef.current);
      viewportIgnoreFallbackTimerRef.current = null;
    }

    const settleIgnoreBounds = () => {
      if (viewportCommandTokenRef.current !== command.id) {
        return;
      }
      ignoreBoundsChangeRef.current = false;
      suppressBoundsCallbackRef.current = false;
      viewportIgnoreFallbackTimerRef.current = null;
    };

    if (command.type === 'fitBounds' && command.northEast && command.southWest && myGoogle) {
      const bounds = new myGoogle.maps.LatLngBounds(
        { lat: command.southWest.lat, lng: command.southWest.lng },
        { lat: command.northEast.lat, lng: command.northEast.lng },
      );
      mapRef.current.fitBounds(bounds, command.edgePadding ?? command.padding ?? 40);
      const currentZoom = mapRef.current.getZoom?.() ?? initialZoom;
      if (Number.isFinite(currentZoom)) {
        if (typeof command.maxZoom === 'number' && currentZoom > command.maxZoom) {
          mapRef.current.setZoom(command.maxZoom);
        }
        if (typeof command.minZoom === 'number' && currentZoom < command.minZoom) {
          mapRef.current.setZoom(command.minZoom);
        }
      }
    } else if (command.type === 'centerZoom' && command.center) {
      mapRef.current.panTo({ lat: command.center.lat, lng: command.center.lng });
      if (typeof command.zoom === 'number') {
        mapRef.current.setZoom(command.zoom);
      }
    }

    let idleListener: any;
    if (myGoogle?.maps?.event?.addListenerOnce) {
      idleListener = myGoogle.maps.event.addListenerOnce(mapRef.current, 'idle', settleIgnoreBounds);
    }

    viewportIgnoreFallbackTimerRef.current = setTimeout(settleIgnoreBounds, 1500);

    return () => {
      if (viewportIgnoreFallbackTimerRef.current) {
        clearTimeout(viewportIgnoreFallbackTimerRef.current);
        viewportIgnoreFallbackTimerRef.current = null;
      }
      if (idleListener && myGoogle?.maps?.event?.removeListener) {
        myGoogle.maps.event.removeListener(idleListener);
      }
    };
  }, [props.viewportCommand, mapReady, mapGeneration]);

  // Reconcile markers by identity instead of rebuilding the map on viewport updates.
  useEffect(() => {
    if (!mapReady || !mapRef.current || !myGoogle) return;
    let cancelled = false;
    const map = mapRef.current;

    const addMarkers = async () => {
      const { AdvancedMarkerElement } = await myGoogle.maps.importLibrary('marker');
      if (cancelled || map !== mapRef.current) return;

      if (!markerClustererRef.current) {
        markerClustererRef.current = new MarkerClusterer({
          markers: [], map,
          algorithm: createSafeViewportAlgorithm({ radius: 160, maxZoom: 18 }),
          renderer: new CustomRenderer(),
        });
        myGoogle.maps.event.addListener(markerClustererRef.current, 'click', (cluster: { markers: { id: string }[] }) => {
          const { onFilterMissionList, onClusterClick } = callbackRefs.current;
          onClusterClick?.();
          ignoreNextBoundsFromClusterRef.current = true;
          onFilterMissionList?.(cluster.markers.map((x) => x.id).filter(Boolean));
        });
      }
      const clusterer = markerClustererRef.current;
      const items = (markerItems ?? []).filter((m) => Number.isFinite(m.coordinate?.latitude) && Number.isFinite(m.coordinate?.longitude));
      const nextKeys = new Map(items.map((item) => [item.id, getMarkerRenderKey(item)]));
      const removed = markersArrayRef.current.filter((marker) => nextKeys.get(marker.id) !== marker.renderKey);
      clusterer.removeMarkers(removed.filter((marker) => !marker.isDirectMapMarker), true);
      removed.forEach((marker) => {
        if (selectedMarkerRef.current === marker) selectedMarkerRef.current = null;
        myGoogle.maps.event.clearInstanceListeners(marker);
        marker.map = null;
      });
      const retained = markersArrayRef.current.filter((marker) => nextKeys.get(marker.id) === marker.renderKey);
      let clusterMembershipChanged = false;
      const returningToCluster: any[] = [];
      retained.forEach((marker) => {
        const isDirectMapMarker = shouldRenderMarkerOutsideCluster(marker, selectedIdRef.current, props.disableClustering);
        if (marker.isDirectMapMarker === isDirectMapMarker) return;
        clusterMembershipChanged = true;
        if (isDirectMapMarker) {
          clusterer.removeMarkers([marker], true);
        } else {
          marker.map = null;
          returningToCluster.push(marker);
        }
        marker.isDirectMapMarker = isDirectMapMarker;
      });
      const retainedIds = new Set(retained.map((marker) => marker.id));
      const added: any[] = [];
      items.forEach((obj) => {
        if (retainedIds.has(obj.id)) return;
        const pinEl = getPinElement(obj);
        if (!pinEl) return;
        const isDirectMapMarker = shouldRenderMarkerOutsideCluster(obj, selectedIdRef.current, props.disableClustering);
        const marker = new AdvancedMarkerElement({
          position: { lat: obj.coordinate.latitude, lng: obj.coordinate.longitude },
          content: pinEl,
          title: obj.accessibilityLabel,
          map: isDirectMapMarker ? map : undefined,
        });
        marker.id = obj.id;
        marker.renderKey = getMarkerRenderKey(obj);
        marker.missionType = obj.missionType;
        marker.isFlashMission = obj.isFlashMission;
        marker.isReferral = obj.isReferral;
        marker.isCompetitorPreview = obj.isCompetitorPreview;
        marker.isDirectMapMarker = isDirectMapMarker;
        marker.competitorCircuitRole = obj.competitorCircuitRole;
        marker.addListener('click', () => {
          const { markerClick, onAirportBannerPress } = callbackRefs.current;
          if (obj.isCompetitorPreview) {
            return;
          }
          if (obj.missionType === MissionType.Airport) {
            clearSelectedMarker();
            setSelectedId(obj.id);
            setAirportActive(true);
            markersArrayRef.current.filter((m) => getMarkerMissionType(m) === MissionType.Airport).forEach((m) => setMarkerVisualSelected(m, false));
            airportActiveRef.current = true;
            onAirportBannerPress?.(obj.id);
            return;
          }

          const accepted = markerClick?.(obj.id, obj.coordinate.latitude, obj.coordinate.longitude, 'map-marker');
          if (accepted === false) {
            return;
          }

          clearSelectedMarker();
          setSelectedId(obj.id);
          setAirportActive(false);
          setMarkerVisualSelected(marker, obj.isFlashMission);
          marker.zIndex = (marker.zIndex ?? 0) + 1;
          selectedMarkerRef.current = marker;
          selectedFlashRef.current = !!obj.isFlashMission;
        });
        added.push(marker);

        if (obj.id === selectedIdRef.current && obj.missionType !== MissionType.Airport) {
          setMarkerVisualSelected(marker, obj.isFlashMission);
          selectedMarkerRef.current = marker;
          selectedFlashRef.current = !!obj.isFlashMission;
        }
        if (obj.missionType === MissionType.Airport && airportActiveRef.current) {
          setMarkerVisualSelected(marker, false);
        }
      });

      markersArrayRef.current = [...retained, ...added];
      const addedClusterMarkers = [...returningToCluster, ...added.filter((marker) => !marker.isDirectMapMarker)];
      clusterer.addMarkers(addedClusterMarkers, true);
      if (clusterMembershipChanged || removed.some((marker) => !marker.isCompetitorPreview) || addedClusterMarkers.length > 0) {
        clusterer.render();
      }
      // Rendering removes former single-marker clusters. Attach direct pins after it.
      markersArrayRef.current.filter((marker) => marker.isDirectMapMarker).forEach((marker) => { marker.map = map; });
    };

    addMarkers();
    return () => { cancelled = true; };
  }, [mapReady, mapGeneration, markerItems, props.disableClustering, selectedId]);

  // GPS updates must not recreate mission markers or the cluster manager.
  useEffect(() => {
    if (!mapReady || !mapRef.current || !myGoogle) return;
    let cancelled = false;
    const map = mapRef.current;
    const updateUserLocation = async () => {
      if (!props.showUserLocation || !userLocationPosition) {
        clearUserLocationMarker();
        return;
      }
      const { AdvancedMarkerElement } = await myGoogle.maps.importLibrary('marker');
      if (cancelled || map !== mapRef.current) return;
      const position = { lat: userLocationPosition.lat, lng: userLocationPosition.lng };
      if (userLocationMarkerRef.current) {
        userLocationMarkerRef.current.position = position;
        return;
      }
      const pinEl = document.createElement('div');
      const svg = parser?.parseFromString(pinCurrentLocationImage, 'image/svg+xml').documentElement;
      if (svg) {
        svg.style.fill = '#0c87da';
        svg.style.height = '24px';
        svg.style.width = '24px';
        pinEl.appendChild(svg);
      }
      userLocationMarkerRef.current = new AdvancedMarkerElement({ position, content: pinEl, map });
    };
    updateUserLocation();
    return () => { cancelled = true; };
  }, [mapReady, mapGeneration, props.showUserLocation, userLocationPosition?.lat, userLocationPosition?.lng]);

  useEffect(() => {
    if (!mapReady) {
      return;
    }

    if (!selectedId) {
      clearSelectedMarker();
      setAirportActive(false);
      return;
    }

    const marker = markersArrayRef.current.find((m) => m.id === selectedId);
    if (!marker) {
      return;
    }

    if (getMarkerMissionType(marker) === MissionType.Airport) {
      clearSelectedMarker();
      setAirportActive(true);
      markersArrayRef.current.filter((m) => getMarkerMissionType(m) === MissionType.Airport).forEach((m) => setMarkerVisualSelected(m, false));
      return;
    }

    setAirportActive(false);
    clearSelectedMarker();
    setMarkerVisualSelected(marker, !!marker.isFlashMission);
    marker.zIndex = (marker.zIndex ?? 0) + 1;
    selectedMarkerRef.current = marker;
    selectedFlashRef.current = !!marker.isFlashMission;
  }, [selectedId, mapReady, markerItems]);
  // Clean selection from parent.
  useEffect(() => {
    if (props.cleanSelection) {
      clearSelectedMarker();
      setAirportActive(false);
      setSelectedId(undefined);
      props.onCleanSelection?.(true);
    }
  }, [props.cleanSelection, props.onCleanSelection]);

  // Reset airport flow from parent.
  useEffect(() => {
    if (!props.resetAirportFlowKey) return;
    setAirportActive(false);
  }, [props.resetAirportFlowKey]);

  useEffect(() => {
    return () => {
      if (mapRef.current) myGoogle?.maps?.event?.clearInstanceListeners?.(mapRef.current);
      removeMapGestureListenersRef.current?.();
      if (boundsTimerRef.current) {
        clearTimeout(boundsTimerRef.current);
        boundsTimerRef.current = null;
      }
      if (viewportIgnoreFallbackTimerRef.current) {
        clearTimeout(viewportIgnoreFallbackTimerRef.current);
        viewportIgnoreFallbackTimerRef.current = null;
      }
      clearUserLocationMarker();
      markerClustererRef.current?.clearMarkers(true);
      (markerClustererRef.current as (MarkerClusterer & { setMap(map: null): void }) | null)?.setMap(null);
      if (markerClustererRef.current) myGoogle?.maps?.event?.clearInstanceListeners?.(markerClustererRef.current);
      markerClustererRef.current = null;
      markersArrayRef.current.forEach((marker) => {
        myGoogle?.maps?.event?.clearInstanceListeners?.(marker);
        marker.map = null;
      });
      markersArrayRef.current = [];
    };
  }, []);

  function clearSelectedMarker() {
    if (!selectedMarkerRef.current) return;
    setMarkerVisualDefault(selectedMarkerRef.current, selectedFlashRef.current);
    selectedMarkerRef.current = null;
  }

  function clearUserLocationMarker() {
    if (!userLocationMarkerRef.current) return;
    userLocationMarkerRef.current.map = null;
    userLocationMarkerRef.current.setMap?.(null);
    userLocationMarkerRef.current = null;
  }

  function setAirportActive(active: boolean) {
    airportActiveRef.current = active;
    if (!active) {
      markersArrayRef.current.filter((m) => getMarkerMissionType(m) === MissionType.Airport).forEach((m) => setMarkerVisualDefault(m, false));
    }
  }

  const handlePanToOrigin = useCallback(() => {
    if (!mapRef.current) return;
    mapRef.current.panTo({ lat: panToOriginPosition.lat, lng: panToOriginPosition.lng });
    mapRef.current.setZoom(initialZoom);
    props.onPanCenter?.();
  }, [panToOriginPosition, initialZoom, props.onPanCenter]);

  const handleZoomIn = useCallback(() => {
    if (!mapRef.current) return;
    handleUserInteractionStart();
    const z = mapRef.current.getZoom();
    mapRef.current.setZoom(z + 1);
  }, [handleUserInteractionStart]);

  const handleZoomOut = useCallback(() => {
    if (!mapRef.current) return;
    handleUserInteractionStart();
    const z = mapRef.current.getZoom();
    mapRef.current.setZoom(Math.max(1, z - 1));
  }, [handleUserInteractionStart]);

  return (
    <View style={styles.container}>
      <View ref={mapViewRef} style={styles.container} />
      <MapControls hidePanToOrigin={props.hidePanToOrigin} onZoomIn={handleZoomIn} onZoomOut={handleZoomOut} onPanToOrigin={handlePanToOrigin} />
    </View>
  );
};

export default WebMap;
